import { useCallback, useContext } from 'react'

import BaseBookmarkButton from '../../Common/BaseBookmarkButton'
import SquareBookmarkButton from '../../Common/SquareBookmarkButton'

import { trackClientEvent } from 'analytics/analytics'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import noop from 'lib/function/noop'
import { clearRecentlySavedTripId } from 'storage/recentSavedTrip'
import ExploreQueryContext from 'tripPlanner/contexts/Explore/ExploreQueryContext'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import { useDeleteTripItem, useCreateTripItem } from 'tripPlanner/hooks/api/tripItem'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import type { ExploreItem, FullTrip } from 'tripPlanner/types/common'
import { getMatchingSavedExploreItem } from 'tripPlanner/utils/explore'
import { createExploreItemBookmarkPayload } from 'tripPlanner/utils/itemConversions/bookmarks/exploreItem'

export interface Props {
  exploreItem: ExploreItem
  variant: 'text' | 'icon'
  tripId?: string
  onSnackbarViewClick?: () => void
}

function ExploreBookmarkButton({
  exploreItem,
  variant,
  tripId: tripIdProp,
  onSnackbarViewClick = noop,
}: Props) {
  const contextTripId = useTripId()
  const tripId = tripIdProp ?? contextTripId
  const { data: trip } = useTrip({ tripId })
  const { canEdit } = useTripPermissions(tripId)

  const savedItem = trip ?
      getMatchingSavedExploreItem(trip, exploreItem) :
    undefined
  const isSaved = !!savedItem

  const {
    showSaveSuccessSnackbar,
    showSaveErrorSnackbar,
    showRemoveSuccessSnackbar,
    showRemoveErrorSnackbar,
  } = useBookmarkSnackbarHandlers()

  const { mutate: deleteBookmark, isLoading: isDeleting } = useDeleteTripItem({
    onSuccess: (_res) => {
      showRemoveSuccessSnackbar(trip!.id, trip!.name, onSnackbarViewClick)
    },
    onError: (_res, _vars) => {
      showRemoveErrorSnackbar(trip!.name)
    },
  })

  const { mutateAsync: createItem, isLoading: isCreating } = useCreateTripItem({
    onError: () => {
      showSaveErrorSnackbar(trip!.name)

      // Clear the recently saved trip ID in case it errored because the trip was deleted
      clearRecentlySavedTripId()
    },
  })

  const { category } = useContext(ExploreQueryContext)

  const createTripItem = useCallback(
    async(trip: FullTrip) => {
      try {
        const isRestaurantOrCafe =
          category.key === 'restaurants' || category.key === 'cafes'
        const type = isRestaurantOrCafe ? 'RESTAURANT_BAR' : 'ATTRACTION'

        const payload = createExploreItemBookmarkPayload({
          exploreItem,
          type,
        })
        const createdItem = await createItem({
          tripId,
          tripItem: payload,
        })
        showSaveSuccessSnackbar(
          trip.id,
          trip.name,
          createdItem.id,
          undefined,
          undefined,
          onSnackbarViewClick,
        )

        trackClientEvent({
          action: 'click',
          subject: 'save_explore_item',
          category: AnalyticsPage.tripPlanner,
          type: 'interaction',
        })
      } catch {
        // Do nothing - failure case is handled by the hook's onError handler
      }
    },
    [
      category.key,
      exploreItem,
      createItem,
      tripId,
      showSaveSuccessSnackbar,
      onSnackbarViewClick,
    ],
  )

  const onButtonClick = useCallback(async() => {
    if (!trip) {
      return
    }
    if (savedItem) {
      deleteBookmark({
        tripId: trip.id,
        tripItemId: savedItem.id,
      })
    } else {
      createTripItem(trip)
    }
  }, [trip, savedItem, deleteBookmark, createTripItem])

  if (!canEdit || !tripId) {
    return null
  }

  const Button = variant === 'icon' ? BaseBookmarkButton : SquareBookmarkButton

  return (
    <Button
      isSaved={isSaved}
      onClick={onButtonClick}
      isProcessing={isDeleting || isCreating}
    />
  )
}

export default ExploreBookmarkButton
